/**
 * 设置页「数据备份 / 恢复」的窄接口与浏览器实现（T13d / 加密备份）。
 *
 * 「触发下载」被收敛成一个只有一个方法的窄接口 `BackupDownloader`：
 * 页面默认用 `browserBackupDownloader`，测试可以注入假实现捕获文件名与 Blob
 * （jsdom 没有真实下载，也不应依赖 `URL.createObjectURL`）。
 */

/** 触发一次文件下载（唯一职责：把 Blob 交给浏览器另存为文件）。 */
export interface BackupDownloader {
  trigger(filename: string, content: Blob): void;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** 备份文件名：`zhixin-backup-YYYYMMDD-HHmm.zxbak`（本地时间）。 */
export function buildBackupFilename(date: Date = new Date()): string {
  const yyyy = String(date.getFullYear());
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  return `zhixin-backup-${yyyy}${mm}${dd}-${hh}${mi}.zxbak`;
}

/** 用 Blob + 临时 <a download> 把备份另存为文件。 */
export const browserBackupDownloader: BackupDownloader = {
  trigger(filename: string, content: Blob): void {
    const blob = content.type === '' ? new Blob([content], { type: 'application/octet-stream' }) : content;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

/** 读取用户选择的备份文件字节（加密容器 / 旧 JSON 都用）。 */
export async function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result: unknown = reader.result;
      if (result !== null && typeof result === 'object' && 'byteLength' in result) {
        resolve(new Uint8Array(result as ArrayBuffer));
      } else {
        reject(new Error('读取备份文件失败'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('读取备份文件失败'));
    reader.readAsArrayBuffer(file);
  });
}

/** 读取用户选择的备份文件文本（优先 `File.text()`，否则回退 FileReader）。 */
export function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('读取备份文件失败'));
    reader.readAsText(file);
  });
}
